import { format, isAfter, isBefore, isEqual, isValid, parseISO } from "date-fns"
/** Utils */
import Rmbx from "../util"
import { getAsDate, isDateTheNextDay, roundDurationToNearestIncrement } from "./ts-utils"
import { isModifyProdDataChanged } from "../components/custom-dashboards/ProductionTracking/utils"
/** Const */
import { CICO_TYPE_SHIFTS_AND_BREAKS_MAP, RESOURCE_TO_HUMAN_READABLE, STORED_DATE_ONLY_FORMAT } from "./constants"
import {
    MutableSourceData,
    TabbedTableWrapperTabParams,
    tResourceObject,
    tSourceData,
} from "../dashboard-data/types"
import cloneDeep from "clone-deep"
import {
    EmployeeWorkShiftStartStopTime,
    TimekeepingEntry,
    TkSummary,
    TkSummaryTimecard,
    tResourceName,
} from "./types"
import { getGroupKeyInfo, getSourceDataForGroupKeyInfo } from "./ag-grid-grouping-utils"
import { tContext, tGroupKeyInfo } from "../components/custom-dashboards/types"
import {
    iTimelineEntryVersionSummary,
    iWeeklyViewNodeData,
    tCompanyStartStopType,
    tEmployeeWorkShiftStartStopTime,
    tProject,
    tReferenceableState,
} from "../cached-data/types"
import { getFlagEnabled } from "../getFlagValue"
import { CellClassParams, ColDef, Column, GridApi, RowNode } from "ag-grid-community"
import { JsonPointer as jsonpointer } from "json-ptr"
import {
    isCicoException,
    isHoursException,
    isShiftsBreaksException,
} from "../components/custom-dashboards/custom-filters/utils"
import { TkCellData, TkRowData } from "../common/types"
import {
    validateWorkRuleSetAgainstTkData,
    WorkRuleSet,
    WorkRuleSetRule,
    computeMostBeneficialRules,
} from "@rhumbix/rmbx_work_rules"
import { matchAndCombineWorkRuleSets } from "@rhumbix/rmbx_matching"
const isNumber = Rmbx.util.isNumber
type IAggFuncParams = import("ag-grid-enterprise").IAggFuncParams

type tDateKeyParams = {
    value: string
}

export const dateKeyCreator = (params: tDateKeyParams): string => {
    const date = getAsDate(params.value)
    return isValid(date) ? format(date, STORED_DATE_ONLY_FORMAT) : "undefined"
}

export const dateIsSameOrAfter = (dateToCompareTo: string | Date, date: string | Date): boolean => {
    dateToCompareTo = getAsDate(dateToCompareTo)
    date = getAsDate(date)
    return isEqual(date, dateToCompareTo) || isAfter(date, dateToCompareTo)
}

export const dateIsSameOrBefore = (dateToCompareTo: string | Date, date: string | Date): boolean => {
    dateToCompareTo = getAsDate(dateToCompareTo)
    date = getAsDate(date)
    return isEqual(date, dateToCompareTo) || isBefore(date, dateToCompareTo)
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const lockedPeriodCellClassRule = (params: any): boolean => {
    // If there is no data on params - for example, because this is a grouping row -
    // return false
    if (!params.data) return false

    return dateIsSameOrAfter(params.data.date, params.context.currentUser.company_options.lock_end_date)
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const lockedPeriodPivotCellClassRule = (params: any): boolean => {
    if (!isPivotColumn(params.colDef)) {
        return false
    }
    return dateIsSameOrAfter(params.colDef.pivotKeys[0], params.context.currentUser.company_options.lock_end_date)
}

export const sumDurations = (values: Array<string>): string => {
    /*
    Given an array of duration strings in "HH:MM" format, split the strings, combine the totals, return string
    */
    let totalHours = 0
    let totalMinutes = 0
    // go through each val string, split it into hours and minutes, add them to the totals
    values.forEach(val => {
        const timeSplit = val.split(":")
        totalHours += isNumber(timeSplit[0]) ? Number(timeSplit[0]) : 0
        totalMinutes += isNumber(timeSplit[1]) ? Number(timeSplit[1]) : 0
    })
    // if the number of minutes >= to 60, convert minutes to whole hours and set totalMinutes to be the remainder
    if (totalMinutes >= 60) {
        totalHours += Math.floor(totalMinutes / 60)
        totalMinutes = totalMinutes % 60
    }
    return `${totalHours}:${String(totalMinutes).padStart(2, "0")}`
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const isPivotColumn = (colDef: any): boolean => colDef.pivotKeys?.length > 0

/**
 * Determine whether the value changed.
 * Note:
 * - This logic is from custom-table.
 * - If both oldValue and newValue are nullish (undefined or null), it will be treated as the value didn't change.
 * @param {string|number|Array<any>|null} oldValue Old value.
 * @param {string|number|Array<any>|null} newValue New value.
 * @returns True if the value changed.
 */
export const isValueChanged = (
    oldValue: string | number | Array<any> | null,
    newValue: string | number | Array<any> | null
): boolean => {
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        if (oldValue.length !== newValue.length) return true

        // Compare the contents of the arrays element-for-element. If the same elements
        // are present but in a different sequence, that also indicates a change
        return !oldValue.every(function (value, index) {
            return value === newValue[index]
        })
    }

    // Using != so that undefined != null returns false (treating them as the same)
    return (oldValue != null || newValue != null) && oldValue !== newValue
}

/**
 * Get data that has been modified.
 * @param {Object[]} data All the sourceData.
 * @param {string} resource Resource name.
 * @returns {Object} Data that has been modified or errors if any.
 */
export const getModifiedData = (
    data: Readonly<Array<Record<string, any>>>,
    resource: string
): { errors?: boolean; modifiedData?: Array<Record<string, any>> } => {
    if (!data?.length) return {}
    const modifiedData: Array<Record<string, any>> = []
    for (const item of data) {
        if (!item.modified && !item.newRow) continue
        // Check if any errors.
        const { errors } = item
        if (errors && Object.keys(errors).length) return { errors: true }
        // Validate if data has changed for Modify Production view.
        if (resource === "modifyProduction" && !isModifyProdDataChanged(item)) continue

        modifiedData.push(item)
    }

    return { modifiedData }
}

/**
 * Between v22 and v26 of ag-grid, the Aggregator function parameters changed types. This attempts
 * to conform them and make typescript happy
 * @param params
 */
export const getAggParams = (params: IAggFuncParams | any[]): any[] => {
    if (Array.isArray(params)) return params
    return (params as IAggFuncParams).values
}

/**
 * Determine if two rows match.
 * @type {boolean}
 */
export const rowIdsMatch = (rowA: tResourceObject, rowB: tResourceObject, idAttr = "id"): boolean => {
    const idMatch = rowA[idAttr] != null && rowA[idAttr] === rowB[idAttr]
    const gridIdMatch = rowA.gridId != null && rowA.gridId === rowB.gridId
    if (getFlagEnabled("WA-8251-shift-extra-placeholder-duplication")) {
        return rowA?.schema?.id === rowB?.schema?.id && (idMatch || gridIdMatch)
    }
    return idMatch || gridIdMatch
}

/**
 * Callback when the source data in the table has been updated. Specifically
 * written for the weekly tk detail modal so that it would split out the modal's
 * table data from the data feeding the background table. We update the local state
 * with the entire set of the table data plus the list of updates
 * @param sourceData The data feeding the tabbed tables
 * @param sourceDataUpdates The list of updates that have happened to the data in the tables since the
 * modal was opened.
 * @param newRowId The initial grid ID for the new rows being created
 * @param resource Which resource type are we updating
 * @param rowData The new values for the rows in the table
 * @param isDelete Whether this was a delete operation
 * @param initialAdd If this row is just being added, we need to not mess with its isPlaceholder attribute
 * @returns {[newSourceData: *, newUpdates: *, newRow: boolean]} Returns the new set of source data that
 * should feed the tables, the updated set of changes that have happened to the tables since they were
 * opened, and whether this update resulted in new rows
 */
export const sourceDataUpdate = (
    sourceData: tSourceData,
    sourceDataUpdates: tSourceData,
    newRowId: number,
    resource: tResourceName,
    rowData: tResourceObject[],
    isDelete = false,
    initialAdd = false
): { newSourceData: MutableSourceData; newUpdates: tSourceData; numNewRows: number } => {
    const newSourceData = cloneDeep(sourceData) as MutableSourceData
    const newUpdates = cloneDeep(sourceDataUpdates) as MutableSourceData
    let numNewRows = 0

    rowData.forEach(row => {
        let data = row as tResourceObject

        // If this is a new row, we give it a few new attributes
        if (row.gridId === undefined) {
            data = { ...row, gridId: newRowId, newRow: true }
            if (!("isPlaceholder" in data)) data.isPlaceholder = true
            numNewRows += 1
        }
        newRowId++
        if (row.serverErrors?.length) {
            if (!data.errors) (data as any).errors = {}
            data.errors.serverErrors = row.serverErrors
        }
        row.serverErrors = []

        // Here we update the source data for the table
        if (!(resource in newSourceData)) {
            newSourceData[resource] = [data]
        } else {
            const resourceObjs = newSourceData[resource] as tResourceObject[]
            const index = resourceObjs.findIndex(r => rowIdsMatch(r, data))
            // If we can't find a row which matches this data, we add it as a new row
            if (index === -1) {
                resourceObjs.unshift(data)
            } else {
                // If we are deleting, remove the record from source data
                if (isDelete) {
                    resourceObjs.splice(index, 1)
                } else {
                    resourceObjs[index] = data
                    if (!initialAdd) {
                        resourceObjs[index].isPlaceholder = false
                    }
                }
            }
        }
        // We also need to track any updates so that it can be recorded in the database
        // when the user hits Save
        if (!(resource in newUpdates)) {
            newUpdates[resource] = [{ ...data, delete: isDelete }]
        } else if (resource in newUpdates) {
            const resourceObjs = newUpdates[resource] as tResourceObject[]
            const index = resourceObjs.findIndex(row => rowIdsMatch(row, data))
            if (index === -1) {
                resourceObjs.unshift({ ...data, delete: isDelete })
            } else if (data.newRow && isDelete) {
                resourceObjs.splice(index, 1)
            } else {
                resourceObjs[index] = { ...data, delete: isDelete }
            }
            newUpdates[resource] = resourceObjs
        }
    })

    return { newSourceData, newUpdates, numNewRows }
}

// Tabbed Table Wrapper helper functions
export const getTabGridParamsFromProps = (
    settings: Record<string, any>,
    lockedColumns: tGroupKeyInfo[] | null,
    sourceData: tSourceData,
    context: tContext
): TabbedTableWrapperTabParams[] => {
    const tableList: Record<string, any>[] = settings.tableList || []
    const filteredSourceData: tSourceData = lockedColumns
        ? getSourceDataForGroupKeyInfo(sourceData, lockedColumns, context)
        : sourceData
    return tableList.map(table => {
        const sourceDataKey = table.resources[0] as tResourceName
        const sourceDataValue = filteredSourceData[sourceDataKey] || []

        return {
            tabLabel: table.tableName,
            tabId: table.tableName,
            settings: table,
            sourceData: {
                [sourceDataKey]: [...sourceDataValue],
            },
        }
    })
}

export const getTabGridParamsFromSchemas = (
    settings: Record<string, any>,
    sourceData: tSourceData,
    lockedColumns: tGroupKeyInfo[] | null,
    context?: tContext,
    referenceableData?: tReferenceableState
): TabbedTableWrapperTabParams[] => {
    let schemaList: Record<string, any>[] = []
    const employeeSchemas = Object.values(referenceableData?.employeeSchemas ?? {})
    if (settings.includeTablesForAllSchemas) {
        schemaList = employeeSchemas
    } else if (settings.tableSchemaList) {
        schemaList = employeeSchemas.filter(ele => settings.tableSchemaList.includes(ele.name))
    }

    let schemaGridParams = schemaList.map(schema => {
        let employeeEntries = [] as tResourceObject[]
        if (sourceData.employeeEntries) {
            employeeEntries = sourceData.employeeEntries.filter(row => {
                // Make sure the row has a schema property - for example, dummy rows will not -
                // and then check if it's a match
                if (row.schema) {
                    const id = isNumber(row.schema) ? row.schema : row.schema.id
                    return id && id === schema.id
                }

                return false
            })
        }
        const schemaSourceData = { employeeEntries }
        const filteredSchemaSourceData = getSourceDataForGroupKeyInfo(schemaSourceData, lockedColumns, context)
        return {
            tabLabel: schema.name,
            tabId: `SE-${schema.id}`,
            schema: schema,
            sourceData: filteredSchemaSourceData,
        }
    })

    // Prune any tab that should not be displayed, specifically if
    // none of the projects in this query can use that schema. The
    // exception is if the schema already has some employeeEntries in place -
    // for example, because the user created some shift extras
    // and then removed that schema from their project
    if (context && Array.isArray(context.filters.projectId) && context.filters.projectId?.length > 0) {
        // If the view is filtered by project, only check those projects
        schemaGridParams = schemaGridParams.filter(
            (sgp: Record<string, any>) =>
                sgp.sourceData["employeeEntries"]?.length > 0 ||
                sgp.schema.projects.find(
                    (projectId: number) =>
                        Array.isArray(context.filters.projectId) &&
                        context.filters.projectId.find((id: number) => id === projectId)
                )
        )
    } else {
        // ... otherwise, check against all the projects in the list view's referenceableData
        const projectList = Object.values(referenceableData?.projects ?? {})

        schemaGridParams = schemaGridParams.filter(
            (sgp: Record<string, any>) =>
                sgp.sourceData["employeeEntries"]?.length > 0 ||
                sgp.schema.projects.find((projectId: number) => projectList.find(proj => proj.id === projectId))
        )
    }

    return schemaGridParams
}

/**
 * Get the parameters for each table.
 * @param settings settings for all the tabs
 * @param lockedColumns a list of the locked columns
 * @param sourceData the data feeding all the tabs of the table
 * @param context the table context
 * @param referenceableData All the referenceable data
 * @param sourceDataIsPrefiltered Whether the provided source data has already been filtered (an optimization)
 * @returns {{tabId: *, settings: *, tabLabel: *, sourceData: {}}[]}
 */
export const getAllTabsGridParams = (
    settings: Record<string, any>,
    lockedColumns: tGroupKeyInfo[] | null,
    sourceData: tSourceData,
    context: tContext,
    referenceableData: tReferenceableState,
    sourceDataIsPrefiltered?: boolean
): TabbedTableWrapperTabParams[] => {
    const tabGridParams: TabbedTableWrapperTabParams[] = getTabGridParamsFromProps(
        settings,
        sourceDataIsPrefiltered ? null : lockedColumns,
        sourceData,
        context
    )
    const schemaTabData = getTabGridParamsFromSchemas(
        settings,
        sourceData,
        lockedColumns,
        context,
        referenceableData
    )
    if (settings?.schemasFirst) {
        schemaTabData.push(...tabGridParams)
        return schemaTabData
    }
    tabGridParams.push(...schemaTabData)
    return tabGridParams
}

/**
 * Simple helper function that takes a set of rows - usually all the rows for a given table -
 * and iterates through to find the highest gridId currently in use. New rows that are manually
 * added to the table can use that as the starting point when they assign new gridIds
 *
 * @param (tResourceObect) rows The table rows to search
 * @returns (number) The highest gridId in these rows, or 1 if no ID was found
 */
export const findHighestGridIdForRows = (rows: readonly tResourceObject[]): number => {
    let gridId = 1
    rows.forEach(row => {
        if (isNumber(row.gridId) && Number(row.gridId) > gridId)
            gridId = typeof row.gridId === "string" ? parseInt(row.gridId) : (row.gridId as number)
    })

    return gridId
}

type ShiftStartStopAGGroupRowNode = {
    allLeafChildren: {
        data: {
            created_on?: string
            company_start_stop_type?: tCompanyStartStopType
        } & tEmployeeWorkShiftStartStopTime
    }[]
}
/**
 * Given an AG Grid row node, finds a Shift row.
 * There may be more than one Shift row if there are multiple EWSs for the same employee represented
 * in the modal. In this case, the Shift row with the earliest created_on date is returned.
 * There may not be a Shift row at all (e.g. the user is currently adding start/stop times for an employee).
 */
export const findShiftRowData = (
    node: ShiftStartStopAGGroupRowNode
): tEmployeeWorkShiftStartStopTime | undefined => {
    return node.allLeafChildren
        .sort((leafA, leafB) => {
            if (!leafA.data.created_on) return 1
            if (!leafB.data.created_on) return -1
            return leafA.data.created_on < leafB.data.created_on ? -1 : 1
        })
        .find(leaf => (leaf.data.company_start_stop_type?.is_break ?? leaf.data.is_break) === false)?.data
}

export type SimplifiedGroupNode = {
    allLeafChildren?: Array<SimplifiedGroupNode>
    childrenAfterGroup?: Array<SimplifiedGroupNode>
    data?: Record<string, any>
}
/**
 * Workaround for agGrid 26 in order to get the data to display for the group node.
 *
 * Due to a change in agGrid 26, after making an edit to a group cell,
 * the value for allLeafChildren becomes an empty list, which prevents us from getting the data we needed.
 * This attempts to workaround it by traversing
 * the children via childrenAfterGroup in order to get a non-empty value for allLeafChildren
 * If grouping by timecard for example:
 * <Timecard Owner>
 *      <Workshift ID>
 *          <Employee>
 *              <Cost Code>
 * The function below will get the row data necessary to fill in the proper values
 * for each of the group nodes (i.e <Timecard Owner>, <Workshift ID>, etc)
 *  https://www.ag-grid.com/react-data-grid/row-object/#reference-groupNodeAttributes-childrenAfterGroup
 * @params groupNode
 * @params depth
 * @params maxDepth
 */
export const findGroupDataFromChildrenAfterGroup = (
    groupNode: SimplifiedGroupNode,
    depth = 0,
    maxDepth = 5
): Record<string, any> | undefined => {
    // we don't want to infinitely recur in the case allLeafChildren is not found,
    // while childrenAfterGroup continues to have a value, so set an arbitrary
    // max depth to stop recurring
    if (depth === maxDepth) {
        return undefined
    } else if (groupNode.allLeafChildren?.length && groupNode.allLeafChildren[0]?.data) {
        return groupNode.allLeafChildren[0]?.data
    } else if (groupNode.childrenAfterGroup?.length) {
        return findGroupDataFromChildrenAfterGroup(groupNode.childrenAfterGroup[0], depth + 1, maxDepth)
    }
    return undefined
}

export const isPlaceholder = (row: Record<string, any>): boolean => row?.isPlaceholder

export const getShiftNodeForEmployee = (
    api: GridApi,
    data: Record<string, any> | undefined
): Record<string, any> | undefined => {
    if (!data) return undefined

    let shiftData: Record<string, any> | undefined = undefined

    const employeeId = getObjectIdOrTempId(data.employee)
    const workshiftId = getObjectIdOrTempId(data.work_shift_id)

    // Search all of the rows in the grid to find the shift node that matches the given employee and workshift
    api.forEachNode(({ data: rowData }) => {
        if (!rowData) return

        // We only are looking for shifts (where is_break is false)
        if (!(rowData.is_break === false || rowData.company_start_stop_type?.is_break === false)) return

        // We only care about rows for the given employee
        if (!(employeeId === getObjectIdOrTempId(rowData.employee))) return

        // We only care about rows for the given workshift
        if (!(workshiftId === getObjectIdOrTempId(rowData.work_shift_id))) return

        shiftData = rowData
    })
    return shiftData
}

// Remove this function when removing flag WA-8799-workshift-date-fix, superseded by getShiftNodeForEmployee
export const doesShiftEndingNextDayExist = (api: GridApi, workShiftDate: Date, employeeId: number): boolean => {
    let nextDayShiftEnd = false
    api.forEachNode(rowNode => {
        const stopTime = parseISO(rowNode.data?.stop_time)
        const rowEmployeeId = isNumber(rowNode.data?.employee) ? rowNode.data?.employee : rowNode.data?.employee?.id
        const isBreak = rowNode.data?.company_start_stop_type?.is_break || rowNode.data?.is_break
        if (
            isBreak === false &&
            stopTime &&
            employeeId &&
            employeeId === rowEmployeeId &&
            isDateTheNextDay(workShiftDate, stopTime)
        )
            nextDayShiftEnd = true
    })
    return nextDayShiftEnd
}

/**
 * A common function for the slide toggle column editor and renderer
 * @param row {RowNode} The Ag Grid row node being affected
 * @param context {tContext} The table context
 * @param colDef {ColDef} The Ag Grid column definition
 * @param gridApi {GridApi} The Ag Grid Grid API
 * @param currentValue {string[] | boolean} The cell's current value
 * @param setCurrentValue {Function} Callback function to set the new value
 * @param isListValueType {boolean} Whether this slider represents a list value. This is the case for the
 * TK Statuses dashboard with the user role toggles.
 * @param onValue {string} The value to set if the slider is on
 */
export const slideToggleHandleChange = (
    row: RowNode,
    context: tContext,
    colDef: ColDef,
    gridApi: GridApi,
    currentValue: string[] | boolean,
    setCurrentValue: (newValue: string[] | boolean) => void,
    isListValueType?: boolean,
    onValue?: string
): void => {
    if (row && context && colDef && gridApi) {
        if (colDef?.field === "/options/show_new_field_names") {
            const title = currentValue ? "Revert to Classic Form Names" : "Update to Newest Field Names"
            const revertToClassic = `Turning this option off will change the data returned from
                        the shift_extra_entries, project_entries (api v2), and workflow_entries (api v3) endpoints,
                        and could break existing integrations. `
            const updateToNewest = `Turning this option on could break existing integrations that
                        rely on shift_extra_entries, project_entries (api v2), and workflow_entries (api v3).`
            const description = currentValue ? revertToClassic : updateToNewest
            context.createModalAction({
                title: title,
                description: description,
                action: () => {
                    row.setDataValue("/options/show_new_field_names", !currentValue)
                    gridApi.clearFocusedCell()
                },
                buttonClass: currentValue ? "continueButton" : "",
                close: () => {
                    context.createModalAction(null)
                    gridApi.clearFocusedCell()
                },
            })
        } else if (isListValueType && onValue) {
            const value = jsonpointer.get(row.data, colDef.field as string) as string[]
            let newValue
            if (!value) newValue = [onValue]
            else {
                const index = value.indexOf(onValue)
                if (index > -1) {
                    newValue = value.filter(role => role !== onValue)
                } else {
                    newValue = [...value, onValue]
                }
            }
            setCurrentValue(newValue)
            gridApi.clearFocusedCell()
        } else if (colDef.field) {
            setCurrentValue(!currentValue)
            gridApi.clearFocusedCell()
        }
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const rowHasErrors = (row: tResourceObject): boolean =>
    !!(
        row.errors &&
        Object.values(row.errors).reduce((acc: boolean, errors) => acc || !!(errors as string[]).length, false)
    )

export const rowHasAlerts = (row: tResourceObject): boolean =>
    !!(
        row.alerts &&
        Object.values(row.alerts).reduce((acc: boolean, alerts) => acc || !!(alerts as string[]).length, false)
    )

export const getDescriptionOfSourceData = (
    sourceData: tSourceData
): { description: string; numRecords: number } => {
    let description = ""
    let numRecords = 0
    Object.entries(sourceData).forEach(([resource, records]) => {
        numRecords += records.length
        description += records.length
            ? `&emsp;${records.length} ${
                  RESOURCE_TO_HUMAN_READABLE[
                      resource as "absences" | "timekeepingEntries" | "employeeEntries" | "ewsStartStopTimes"
                  ][records.length == 1 ? "single" : "plural"]
              }<br>`
            : ""
    })
    return { description, numRecords }
}

/**
 * Get the object id from an object attribute. Sometimes it's an ID already, sometimes it's an object
 * @param obj
 */
export const getObjectId = (obj: Record<string, any> | number): number =>
    obj && typeof obj === "object" ? obj.id : obj

/**
 *  Gets the object id or temp id from an object attribute.  Sometimes it's an ID already, sometimes it's an object,
 *  and sometimes there's a tempId that is used instead of an id
 *  (such as in the case of workshifts in create time card modal).
 * @param obj
 * @returns number
 */
export const getObjectIdOrTempId = (obj: Record<string, any> | number): number => {
    if (typeof obj === "number") return obj

    if (obj && typeof obj === "object") {
        if (obj.id) return obj.id
        if (obj.tempId) return obj.tempId
    }

    // Always return a number, in this case -1 to indicate no ID was found
    return -1
}

/**
 * Check whether some cell data is compatible with another column. Used when trying to find a match from
 * copying/pasting a range of data. Normally it's enough to just compare source types, but in the case of
 * an enum, we want to make sure the data is one of the enum choices.
 * @param columnType {string} The type of the destination column
 * @param destCell {Record<string, any>} The potential source data
 * @param colDef {ColDef} Column definition for the destination column
 * @param value {any} The value we're trying to paste
 */
export const cellValuesAreCompatible = (
    columnType: string,
    destCell: Record<string, any>,
    colDef: ColDef,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    value: any
): boolean =>
    columnType === destCell.sourceType &&
    (!colDef.cellEditorParams?.values || colDef.cellEditorParams?.values.includes(value))

/**
 * Take the CI/CO data from the back-end and make it into the expected format for Shifts + Breaks.
 * We filter out anything that isn't a shift, meal or break start.
 * @param cicoRows {tResourceObject[]} The cico data from the API
 */
export const convertCicoToShiftAndBreakOld = (cicoRows: readonly tResourceObject[]): tResourceObject[] => {
    const clockedInDuration = cicoRows.reduce((agg, row) => {
        agg[row.employee] = (agg[row.employee] || 0) + (row.clocked_in_duration || 0)
        return agg
    }, {}) as Record<string, number>
    cicoRows.forEach(cicoRow => {
        cicoRow.start_time = cicoRow.entered_time
        cicoRow.is_cico_row = true

        // This is probably a bit broken with CHANGE_TASK events
        const duration = cicoRow.entry_type === "CLOCK_IN" ? clockedInDuration[cicoRow.employee] : cicoRow.duration
        cicoRow.stop_time = new Date(new Date(cicoRow.start_time).getTime() + duration * 60000).toISOString()
        switch (cicoRow.entry_type) {
            case "CLOCK_IN":
            case "CHANGE_TASK":
                cicoRow.start_stop_type = "Shift"
                cicoRow.is_break = false
                break
            case "START_MEAL":
                cicoRow.start_stop_type = "Meal"
                cicoRow.is_break = true
                break
            case "START_BREAK":
                cicoRow.start_stop_type = "Break"
                cicoRow.is_break = true
                break
            default:
                cicoRow.duration_minutes = 0
        }
    })

    return cicoRows.filter(r =>
        ["CLOCK_IN", "START_MEAL", "START_BREAK", "CHANGE_TASK"].includes(r.entry_type)
    ) as tResourceObject[]
}

/**
 * Take the CI/CO data from the back-end and make it into the expected format for Shifts + Breaks.
 * We filter out anything that isn't a shift, meal or break start.
 * @param cicoRows {tResourceObject[]} The cico data from the API
 * @param isRealRow {boolean} Whether this is a real row or a visual aide showing the CICO data
 * @param startStopTypes {tResourceObject[]} The start/stop types from the API
 * @param outputNewRows {boolean} Whether to output new rows or just modify the existing ones
 */
export const convertCicoToShiftAndBreak = (
    cicoRows: readonly tResourceObject[],
    isRealRow?: boolean,
    startStopTypes?: readonly tResourceObject[],
    outputNewRows?: boolean
): tResourceObject[] => {
    const clockedInDuration = cicoRows.reduce((agg, row) => {
        agg[row.employee] = (agg[row.employee] || 0) + (row.clocked_in_duration || 0)
        return agg
    }, {}) as Record<string, number>
    const newCicoRows: tResourceObject[] = []
    cicoRows.forEach(cicoRow => {
        // For some uses, we don't really care about the extra cico data in our rows
        // however, if we're creating new entities from the cico data, we need to start clean and not
        // bring along things like ids and such.
        const newCicoRow = outputNewRows ? {} : cicoRow
        newCicoRow.start_time = cicoRow.entered_time
        if (isRealRow) {
            newCicoRow.status = "PENDING"
        } else {
            newCicoRow.is_cico_row = true
        }
        newCicoRow.entry_type = cicoRow.entry_type
        newCicoRow.employee = cicoRow.employee
        newCicoRow.date = format(getAsDate(newCicoRow.start_time), STORED_DATE_ONLY_FORMAT)

        // This is probably a bit broken with CHANGE_TASK events
        const duration = cicoRow.entry_type === "CLOCK_IN" ? clockedInDuration[cicoRow.employee] : cicoRow.duration
        newCicoRow.stop_time = new Date(new Date(newCicoRow.start_time).getTime() + duration * 60000).toISOString()
        switch (cicoRow.entry_type) {
            case "CLOCK_IN":
            case "CHANGE_TASK":
                newCicoRow.start_stop_type = "Shift"
                newCicoRow.is_break = false
                break
            case "START_MEAL":
                newCicoRow.start_stop_type = "Meal"
                newCicoRow.is_break = true
                break
            case "START_BREAK":
                newCicoRow.start_stop_type = "Break"
                newCicoRow.is_break = true
                break
            default:
                newCicoRow.duration_minutes = 0
        }
        if (startStopTypes) {
            newCicoRow.company_start_stop_type = startStopTypes.find(sst => sst.name === newCicoRow.start_stop_type)
        }
        newCicoRows.push(newCicoRow)
    })

    return newCicoRows.filter(r =>
        ["CLOCK_IN", "START_MEAL", "START_BREAK", "CHANGE_TASK"].includes(r.entry_type)
    ) as tResourceObject[]
}

/**
 * Takes the shift and break data along with the cico data and groups the rows together. We loop through
 * the shifts and breaks and try to find a cico entry that matches the employee and type. If there are more
 * than one (like multiple meals or breaks), we match with the closest start time. We also keep track of which
 * ones we've used so we don't double-up. If there isn't a match, we fake a row indicating that that is the case
 * @param cicoData {tResourceObject[]} Timeline entry version data from the API
 * @param shiftsAndBreaks {tResourceObject[]} Employee start/stop times from the API
 * @param showCico {boolean} Whether we are showing the cico rows in the UI. We still need this function
 * @param referenceableData {tReferenceableState} The referenceable data set (used for looking up timecards and
 * projects)
 * to compute the cico delta column in any case.
 */
export const sortShiftsAndBreaksWithCicoData = (
    cicoData: tResourceObject[],
    shiftsAndBreaks: readonly tResourceObject[],
    showCico: boolean,
    referenceableData: tReferenceableState
): tResourceObject[] => {
    const output: tResourceObject[] = []
    const usedCicoIds = new Set()
    shiftsAndBreaks.forEach((row, idx) => {
        const rowType = CICO_TYPE_SHIFTS_AND_BREAKS_MAP[row.start_stop_type as "Shift" | "Break" | "Meal"]
        const shiftBreakEmployeeId = getObjectId(row.employee)
        let cicoEnabledRow = true
        if (row.work_shift_id) {
            const workShift =
                row.work_shift_id && typeof row.work_shift_id === "object"
                    ? row.work_shift_id
                    : referenceableData.workShifts[getObjectId(row.work_shift_id)]
            cicoEnabledRow = workShift?.projects?.some(
                (pId: number) => referenceableData.projects[pId]?.cico_settings?.enabled
            )
        }
        const matchingCicoRows = cicoEnabledRow
            ? cicoData.filter(cicoRow => {
                  const employeeId = getObjectId(cicoRow.employee)
                  return (
                      employeeId === shiftBreakEmployeeId &&
                      rowType === cicoRow.entry_type &&
                      !usedCicoIds.has(cicoRow.id)
                  )
              })
            : []

        if (matchingCicoRows.length) {
            let minTimeDiff: number | null
            let minIdx = 0
            matchingCicoRows.forEach((cicoRow, idx) => {
                const startTimeDiff = new Date(cicoRow.start_time).getTime() - new Date(row.start_time).getTime()
                if (!minTimeDiff || startTimeDiff < minTimeDiff) {
                    minTimeDiff = startTimeDiff
                    minIdx = idx
                }
            })
            const cicoDuration =
                (new Date(matchingCicoRows[minIdx].stop_time).getTime() -
                    new Date(matchingCicoRows[minIdx].start_time).getTime()) /
                60000
            const rowDuration = (new Date(row.stop_time).getTime() - new Date(row.start_time).getTime()) / 60000
            output.push({
                ...row,
                cico_diff: getFlagEnabled("RN-2134-round-cico-totals")
                    ? (rowDuration - cicoDuration) / 60
                    : ((rowDuration - cicoDuration) / 60).toFixed(2),
            })
            usedCicoIds.add(matchingCicoRows[minIdx].id)
            if (showCico) {
                output.push({ ...matchingCicoRows[minIdx], gridId: (row.gridId as number) * -1 })
            }
        } else {
            output.push({ ...row, cico_diff: (row.duration_minutes || 0) / 60 })
            if (showCico)
                output.push({
                    gridId: idx * -1,
                    id: idx * -1,
                    employee: row.employee,
                    start_stop_type: "No Data Available",
                    duration_minutes: "--",
                    workshiftId: { id: -1 },
                    status: "__NONE__",
                })
        }
    })

    if (showCico)
        cicoData
            .filter(row => !usedCicoIds.has(row.id))
            .forEach(row => {
                output.push({ ...row, gridId: row.id! * -1, cico_diff: 0 })
            })

    return output
}

/**
 * Get the projects and work shifts used in a timekeeping entry row and/or the context of the detail modal
 */
export const getProjectsAndWorkShiftsFromTKRow = (
    row: tResourceObject,
    context: tContext,
    workShiftFilterSet: Set<number>,
    projectFilterSet: Set<number>
): { projectFilterSet: Set<number>; workShiftFilterSet: Set<number> } => {
    if (!context) {
        return { projectFilterSet, workShiftFilterSet }
    }
    let newProjectFilterSet = projectFilterSet
    let newWorkShiftFilterSet = workShiftFilterSet

    if (!row.work_shift_id) {
        const workShiftLockedColumn = context.groupKeyInfo.find(col => col.colDef.field === "/work_shift_id")
        const projectLockedColumn = context.groupKeyInfo.find(col => col.colDef.field === "/project")
        if (workShiftLockedColumn) {
            const workShiftId = getObjectId(workShiftLockedColumn.value)
            newWorkShiftFilterSet = workShiftFilterSet.add(workShiftId)
        } else if (projectLockedColumn) {
            const projectId = getObjectId(projectLockedColumn.value)
            newProjectFilterSet = projectFilterSet.add(projectId)
        }
    } else if (typeof row.work_shift_id === "object") {
        const projectIds = row.work_shift_id.projects.map((p: tProject) => getObjectId(p))
        newProjectFilterSet = projectFilterSet.size
            ? // @ts-ignore
              projectFilterSet.intersection(new Set(projectIds))
            : // @ts-ignore
              projectFilterSet.union(new Set(projectIds))
    } else if (context.referenceableData?.workShifts[row.work_shift_id]) {
        const projectIds = context.referenceableData?.workShifts[row.work_shift_id].projects.map(p =>
            getObjectId(p)
        )
        newProjectFilterSet = projectFilterSet.size
            ? // @ts-ignore
              projectFilterSet.intersection(new Set(projectIds))
            : // @ts-ignore
              projectFilterSet.union(new Set(projectIds))
    } else {
        newWorkShiftFilterSet = workShiftFilterSet.add(row.work_shift_id)
    }
    return { workShiftFilterSet: newWorkShiftFilterSet, projectFilterSet: newProjectFilterSet }
}

/**
 * Reduce the node data for a weekly view cell to a similar format that the table pivot provides. Ideally
 * we would use the same reduceObject function, but that's a bigger change than we want to make right now,
 * especially since the pivoted data already has the date taken into account. We are dealing with an entire
 * row of data.
 * @param nodeData {Record<string, any>} The node data to reduce
 * @param context {tContext} The context object
 * @returns {TkRowData} The reduced node data
 */
export const reduceWeeklyViewNodeData = (
    nodeData: {
        data: iWeeklyViewNodeData<TimekeepingEntry | EmployeeWorkShiftStartStopTime | iTimelineEntryVersionSummary>
    }[],
    context: tContext,
    tkSummary: TkSummary
): TkRowData => {
    const reducedNodeData: TkRowData = nodeData.reduce(
        (
            agg: TkRowData,
            lc: {
                data: iWeeklyViewNodeData<
                    TimekeepingEntry | EmployeeWorkShiftStartStopTime | iTimelineEntryVersionSummary
                >
            }
        ) => {
            if (!agg.byDate[lc.data.date])
                agg.byDate[lc.data.date] = {
                    ST: 0,
                    OT: 0,
                    DT: 0,
                    cicoEntries: [],
                    consecutive_days: 0,
                    weekly_hours: 0,
                    clocked_in_duration: 0,
                    meal_duration: 0,
                    break_duration: 0,
                    cicoTime: 0,
                    edited_by_other: 0,
                    item_count: 0,
                    shift_date: lc.data.date,
                    startStopTypes: new Set(),
                    startStopTimes: [],
                    version_count: 0,
                    cicoByProject: {},
                    workshiftId: new Set(),
                }
            if (lc.data.dataType === "timelineEntryVersions") {
                const timelineEntryVersion = (lc.data as unknown) as iTimelineEntryVersionSummary
                if (getFlagEnabled("RN-2134-round-cico-totals")) {
                    if (timelineEntryVersion.project in agg.byDate[timelineEntryVersion.date].cicoByProject) {
                        agg.byDate[timelineEntryVersion.date].cicoByProject[timelineEntryVersion.project] +=
                            timelineEntryVersion.clocked_in_duration - timelineEntryVersion.meal_duration
                    } else {
                        agg.byDate[timelineEntryVersion.date].cicoByProject[timelineEntryVersion.project] =
                            timelineEntryVersion.clocked_in_duration - timelineEntryVersion.meal_duration
                    }
                }
                agg.clocked_in_duration += timelineEntryVersion.clocked_in_duration
                agg.byDate[timelineEntryVersion.date].version_count += timelineEntryVersion.version_count
                agg.byDate[lc.data.date].item_count += 1
                agg.byDate[timelineEntryVersion.date].clocked_in_duration +=
                    timelineEntryVersion.clocked_in_duration
                agg.byDate[timelineEntryVersion.date].edited_by_other += timelineEntryVersion.edited_by_other
                    ? 1
                    : 0
                agg.byDate[timelineEntryVersion.date].meal_duration += timelineEntryVersion.meal_duration
                agg.byDate[timelineEntryVersion.date].break_duration += timelineEntryVersion.break_duration
                agg.byDate[timelineEntryVersion.date].cicoEntries.push(timelineEntryVersion)
            }
            if (lc.data.dataType === "timekeepingEntries") {
                let cicoEnabled = true
                const timekeepingEntry = (lc.data as unknown) as TimekeepingEntry
                if (getFlagEnabled("WA-8708-cico-improvements") && timekeepingEntry.cost_code) {
                    if (typeof timekeepingEntry.cost_code === "object") {
                        cicoEnabled = timekeepingEntry.cost_code.project!.cico_settings!.enabled
                    } else if (context.referenceableData.costCodes[timekeepingEntry.cost_code]) {
                        const project: tProject =
                            context.referenceableData.costCodes[timekeepingEntry.cost_code].project
                        cicoEnabled = !!project?.cico_settings?.enabled
                    }
                }
                agg.ST += timekeepingEntry.adjusted_minutes_st / 60
                agg.DT += timekeepingEntry.adjusted_minutes_dt / 60
                agg.OT += timekeepingEntry.adjusted_minutes_ot / 60
                agg.byDate[timekeepingEntry.date].ST += timekeepingEntry.adjusted_minutes_st / 60
                agg.byDate[timekeepingEntry.date].DT += timekeepingEntry.adjusted_minutes_dt / 60
                agg.byDate[timekeepingEntry.date].OT += timekeepingEntry.adjusted_minutes_ot / 60
                agg.byDate[timekeepingEntry.date].workshiftId.add(getObjectId(timekeepingEntry.work_shift_id))

                if (cicoEnabled) {
                    agg.byDate[timekeepingEntry.date].cicoTime +=
                        (timekeepingEntry.adjusted_minutes_st +
                            timekeepingEntry.adjusted_minutes_dt +
                            timekeepingEntry.adjusted_minutes_ot) /
                        60
                }
                if (
                    getFlagEnabled("WA-8985-work-rules") &&
                    tkSummary &&
                    tkSummary[getObjectId(timekeepingEntry.employee)]
                ) {
                    const tkSummaryData = (tkSummary[
                        getObjectId(timekeepingEntry.employee)
                    ] as unknown) as TkSummaryTimecard[]
                    const timecardSummary = tkSummaryData.find(
                        (tc: TkSummaryTimecard) => tc.id === getObjectId(timekeepingEntry.work_shift_id)
                    )
                    if (timecardSummary) {
                        agg.byDate[timekeepingEntry.date].consecutive_days = timecardSummary.consecutive_days
                        agg.byDate[timekeepingEntry.date].weekly_hours = timecardSummary.weekly_hours
                    }
                }
            }
            if (lc.data.dataType === "ewsStartStopTimes") {
                const ewsStartStopTime = (lc.data as unknown) as EmployeeWorkShiftStartStopTime
                if (!agg.startStopTypes) agg.startStopTypes = new Set()
                agg.byDate[lc.data.date].startStopTypes.add(ewsStartStopTime.start_stop_type)
                agg.byDate[lc.data.date].startStopTimes.push(ewsStartStopTime)
                if (!agg.byDate[lc.data.date].workshiftId.has(getObjectId(ewsStartStopTime.work_shift_id))) {
                    agg.byDate[lc.data.date].workshiftId.add(getObjectId(ewsStartStopTime.work_shift_id))
                }
            }
            return agg
        },
        {
            ST: 0,
            DT: 0,
            OT: 0,
            cicoEntries: [],
            clocked_in_duration: 0,
            meal_duration: 0,
            break_duration: 0,
            consecutive_days: 0,
            weekly_hours: 0,
            shift_date: "",
            byDate: {},
            cicoByProject: {},
            cicoTime: 0,
            version_count: 0,
            item_count: 0,
            edited_by_other: 0,
            startStopTypes: new Set(),
            startStopTimes: [],
            workshiftId: new Set(),
        } as TkRowData
    )

    if (getFlagEnabled("RN-2134-round-cico-totals") && reducedNodeData) {
        const timekeepingIncrement = context.currentUser.company_options.timekeeping_increment ?? 15
        Object.values(reducedNodeData.byDate).forEach((data: TkCellData) => {
            let roundedCicoTime = 0
            Object.entries(data.cicoByProject).forEach(entry => {
                const [projectId, duration] = entry
                const project = context.referenceableData.projects[(projectId as unknown) as number]
                const roundingIncrement = project?.cico_settings?.rounding_increment ?? 0
                const roundedDuration = roundDurationToNearestIncrement(
                    duration,
                    timekeepingIncrement,
                    roundingIncrement
                )
                roundedCicoTime += roundedDuration
            })
            data.clocked_in_duration = roundedCicoTime
        })
    }
    return reducedNodeData
}

export const getCellSourceData = (rowNode: RowNode, context: tContext, column: Column): Record<string, any> => {
    if (!context.columnApi || !rowNode.childrenAfterGroup) return {}
    const pivotColumns = context.columnApi.getPivotColumns()
    const rowSourceData: Record<string, any> = {}
    rowNode.childrenAfterGroup.forEach(child => {
        child.childrenAfterGroup?.forEach(c => {
            if (!Object.keys(rowSourceData).includes(c.data.dataType)) rowSourceData[c.data.dataType] = []
            rowSourceData[c.data.dataType].push(c.data)
        })
    })
    const groupKeyInfo = getGroupKeyInfo(rowNode, column, pivotColumns, context.referenceableData)
    return getSourceDataForGroupKeyInfo(rowSourceData, groupKeyInfo, context)
}

/**
 * 
    employee: {
        trade?: string
        classification?: string
        union_ids: number[]
        cohort_ids: number[]
        certification_ids?: number[]
        license_ids?: number[]
        custom_fields: Record<string, string>
    }
    project: {
        country?: string
        state?: string
        city?: string
        zip_code?: string
        custom_fields: Record<string, string>
    }
    cost_code?: {
        custom_fields: Record<string, string>
    }
    entry_date: string // YYYY-MM-DD
    shift_start_time?: string // ISO 8601 datetime (e.g., "2024-01-15T08:00:00-05:00"), optional
 * @param rowNode 
 * @param context 
 * @param workRuleSets 
 * @param date 
 * @returns 
 */
export const getApplicableWorkRules = (
    context: tContext,
    workRuleSets: WorkRuleSet[],
    cellSourceData: Record<string, any>
): { project: tProject; workRule: WorkRuleSetRule }[] => {
    const workRuleSetRules: { projectId: number; workRules: WorkRuleSetRule[] }[] = []
    Object.entries(cellSourceData).forEach(([dataType, values]) => {
        let shiftStartTime: string | undefined

        values.forEach((value: any) => {
            if (dataType === "timelineEntryVersions" && value.entry_type === "CLOCK_IN") {
                shiftStartTime = value.entered_time
            }
            const date = value.date
            const project =
                typeof value.project === "object"
                    ? value.project
                    : context.referenceableData.projects[value.project]
            const costCode =
                typeof value.cost_code === "object"
                    ? value.cost_code
                    : context.referenceableData.costCodes[value.cost_code] || null
            const employee =
                typeof value.employee === "object"
                    ? value.employee
                    : context.referenceableData.employees[value.employee]
            const tkContext = {
                employee: {
                    trade: employee.trade,
                    classification: employee.classification,
                    union_ids: employee.unions?.map((union: Record<string, any>) => union.id) || [],
                    cohort_ids: employee.cohort_permissions?.map((cohort: Record<string, any>) => cohort.id) || [],
                    certification_ids:
                        employee.certifications?.map((certification: Record<string, any>) => certification.id) ||
                        [],
                    license_ids: employee.licenses?.map((license: Record<string, any>) => license.id) || [],
                    custom_fields: employee.custom_fields || {},
                },
                project: {
                    country: project?.country,
                    state: project?.state,
                    city: project?.city,
                    zip_code: project?.zip_code,
                    custom_fields: project?.custom_fields || {},
                    timezone: project?.timezone,
                },
                entry_date: date,
                cost_code: {
                    custom_fields: costCode?.custom_fields || {},
                },
                shift_start_time: value.shift_start_time || shiftStartTime,
            }
            const itemRules = matchAndCombineWorkRuleSets(workRuleSets, tkContext)
            workRuleSetRules.push({ projectId: getObjectId(project), workRules: itemRules })
        })
    })
    const mostBeneficialRules = computeMostBeneficialRules(workRuleSetRules)
    return mostBeneficialRules.rules.map(projectRule => ({
        project: context.referenceableData.projects[projectRule.projectId],
        workRule: projectRule.workRule,
    }))
}

export const evaluateWorkRules = (
    value: TkCellData,
    workRules: { project: tProject; workRule: WorkRuleSetRule }[],
    clockedInDuration: number,
    context: tContext,
    date?: string
) => {
    const warnings: string[] = []
    const errors: string[] = []
    if (typeof value !== "object" || !value || Object.keys(value).length === 0 || value.isTotalCell) {
        return { warnings, errors }
    }
    const shiftDate = date || value.shift_date

    if (workRules.length) {
        return validateWorkRuleSetAgainstTkData(workRules, {
            ...value,
            clocked_in_duration: clockedInDuration,
            shift_date: shiftDate.split("T")[0],
            startStopTimes: value.startStopTimes || [],
        })
    }
    return { warnings, errors }
}

export const weeklyViewCellHighlightRule = (
    params: CellClassParams,
    exceptions: Record<string, any>,
    workRuleSets: WorkRuleSet[],
    findMatches: any[]
) => {
    if (!params.context.settings?.otherSettings?.enableFindMatchRule) return false
    const colDef = params.colDef as ColDef & { isTotalCell: boolean }
    let clockedInDuration = 0
    if (
        params.context.currentUser.features.cico &&
        exceptions.cicoDiffException.enabled &&
        params.value &&
        ["/employee", "/project", "/foreman"].includes(params.node.field as string)
    ) {
        const timekeepingIncrement = params.context.currentUser.company_options.timekeeping_increment ?? 15
        clockedInDuration =
            getFlagEnabled("RN-2134-round-cico-totals") && params.value?.cicoByProject
                ? Object.entries(params.value.cicoByProject).reduce((acc, [projectId, minutes]) => {
                      const project = params.context.referenceableData.projects[projectId]
                      const roundingIncrement = project.cico_settings?.rounding_increment
                      const roundedDuration = roundDurationToNearestIncrement(
                          minutes as number,
                          timekeepingIncrement,
                          roundingIncrement
                      )
                      return acc + roundedDuration
                  }, 0)
                : params.value?.clocked_in_duration

        if (
            exceptions.cicoDiffException.enabled &&
            isCicoException(
                exceptions.cicoDiffException,
                {
                    ...params.value,
                    clocked_in_duration: clockedInDuration,
                },
                undefined,
                colDef.isTotalCell
            )
        )
            return true
    }
    if (
        exceptions.workRulesException.enabled &&
        params.context.currentUser.features.work_rules &&
        !colDef.isTotalCell &&
        typeof params.value === "object" &&
        params.value &&
        Object.keys(params.value).length > 0 &&
        params.node.field === "/employee"
    ) {
        const value = { ...params.value }
        for (const timeType of ["ST", "OT", "DT"]) {
            if (!value[timeType]) {
                value[timeType] = 0
            }
        }
        if (!("cicoEntries" in value)) value.cicoEntries = []
        let employeeId: number | undefined = undefined
        // Get the cell source data for the node
        const cellDate = params.colDef.pivotKeys![0]
        // This appears to be row data. We may need to filter it by the cell date.
        const cellSourceData: Record<string, any> = params.node.allLeafChildren.reduce(
            (acc: Record<string, any>, child: RowNode) => {
                if (child.data.date !== cellDate) return acc
                if (!Object.keys(acc).includes(child.data.dataType)) acc[child.data.dataType] = []
                acc[child.data.dataType].push(child.data)
                if (!employeeId) employeeId = getObjectId(child.data.employee)
                return acc
            },
            {}
        )

        // Add in the summary data for the cell
        if (exceptions.tkSummary) {
            const tkSummaryData = exceptions.tkSummary[employeeId!]
            const shiftDate = colDef.pivotKeys![0]
            const timecardSummary = tkSummaryData?.find((tc: TkSummaryTimecard) => tc.shift_date === shiftDate)
            if (timecardSummary) {
                value.consecutive_days = timecardSummary.consecutive_days
                value.weekly_hours = timecardSummary.weekly_hours
            }
        }

        const applicableWorkRules = getApplicableWorkRules(params.context, workRuleSets, cellSourceData)
        const { warnings, errors } = evaluateWorkRules(
            value,
            applicableWorkRules,
            value.clocked_in_duration,
            params.context,
            cellDate
        )
        if ((warnings && warnings.length > 0) || (errors && errors.length > 0)) return true
    } else {
        if (exceptions.hoursException.enabled && params.node.field === "/employee") {
            if (isHoursException(exceptions.hoursException, params.value, undefined, !!colDef.isTotalCell))
                return true
        }
        if (
            exceptions.shiftsBreaksException.enabled &&
            params.node.field === "/employee" &&
            isShiftsBreaksException(exceptions.shiftsBreaksException, params.value, colDef.isTotalCell)
        ) {
            return true
        }
    }
    if (colDef.isTotalCell) return false

    if (colDef.pivotKeys) {
        return findMatches?.some(
            fm => fm.rowNode.rowIndex === params.node.rowIndex && fm.column.colDef.headerName === colDef.headerName
        )
    }
}
